"use strict";
/**
 * The MARK button — the operator's one price sample per order.
 *
 * Sits in the order-utility row above the order buttons (public/index.html), so
 * one control serves every layout and nothing sits near the order buttons. The
 * row keeps a fixed min-height in CSS, so no state here can reflow the page.
 *
 * States: hidden (nothing armed) → armed → pending (in flight) → done/error
 * (shown briefly, then hidden). Visibility here is the ARM state only; whether
 * the tier may see the button at all is a permission gate (gates.ts,
 * `price-sample`, level 4+) applied through a separate class.
 *
 * The arm rules themselves live in orders/price-sample-arm.ts; this module only
 * renders them and sends the press.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.syncFromArmState = syncFromArmState;
exports.initPriceSampleButton = initPriceSampleButton;
exports.resetPriceSampleButtonForTest = resetPriceSampleButtonForTest;
const state_1 = require("../state");
const price_sample_arm_1 = require("../orders/price-sample-arm");
const pnl_1 = require("../api/pnl");
const request_1 = require("../api/request");
const notifications_1 = require("./notifications");
/** How long the ✓ / ✕ stays up before the button hides again. */
const RESULT_LINGER_MS = 2500;
const GLYPH = {
    armed: "◎",
    pending: "⋯",
    done: "✓",
    error: "✕",
};
const LABEL = {
    armed: "MARK",
    pending: "···",
    done: "SAVED",
    error: "FAILED",
};
/** The state classes this module owns — the only ones render() may touch. */
const STATE_CLASSES = ["is-armed", "is-pending", "is-done", "is-error"];
let current = "hidden";
let resultTimer = null;
const button = () => document.getElementById("price-sample-btn");
function render(state) {
    const el = button();
    if (!el)
        return;
    current = state;
    // Add/remove only our own classes. Assigning `className` here wiped the
    // permission gate's `.permission-gated-hidden` (gates.ts) and un-hid the
    // button for a tier that must not see it — the gate re-runs only when
    // permissions/page/amount change, so nothing put it back.
    el.classList.remove(...STATE_CLASSES);
    if (state === "hidden") {
        el.hidden = true;
        return;
    }
    el.hidden = false;
    el.classList.add(`is-${state}`);
    el.disabled = state !== "armed";
    const icon = el.querySelector(".price-sample-icon");
    const label = el.querySelector(".price-sample-label");
    if (icon)
        icon.textContent = GLYPH[state];
    if (label)
        label.textContent = LABEL[state];
}
function clearLinger() {
    if (resultTimer === null)
        return;
    window.clearTimeout(resultTimer);
    resultTimer = null;
}
/** Show a terminal result, then fall back to whatever the arm state says. */
function showResult(state) {
    render(state);
    clearLinger();
    resultTimer = window.setTimeout(() => {
        resultTimer = null;
        syncFromArmState();
    }, RESULT_LINGER_MS);
}
/**
 * Reflect the arm state in the button. Cheap and idempotent: it runs on every
 * app state change (the orderbook poll updates state several times a second),
 * so it must not touch the DOM unless the state actually changed.
 */
function syncFromArmState() {
    // Never stomp an in-flight request or a result that's still showing.
    if (current === "pending" || resultTimer !== null)
        return;
    const next = (0, price_sample_arm_1.getArmedSamples)().length > 0 ? "armed" : "hidden";
    if (next !== current)
        render(next);
}
/**
 * Send one click's sample. Never rejects: the caller needs every click's
 * outcome, not the first failure.
 */
async function sendOne(armed, observed) {
    try {
        const result = await (0, pnl_1.recordUserPriceSample)({
            clickId: armed.clickId,
            observed: {
                venue: armed.venue,
                market: armed.marketId,
                ...observed,
            },
            expectedRequests: armed.expectedRequests,
        });
        return { clickId: armed.clickId, sampled: result.sampled, settled: true };
    }
    catch (error) {
        const status = error instanceof request_1.ApiRequestError ? error.statusCode : 0;
        const settled = status >= 400 && status < 500;
        return { clickId: armed.clickId, sampled: 0, settled };
    }
}
/**
 * Record the displayed book against every click armed on this market — one
 * request each, so a click that fails transiently stays armed while the rest
 * are spent. Each request is independently idempotent (the DB's partial unique
 * index), so a retry can never double-record.
 */
async function handlePress() {
    const armed = (0, price_sample_arm_1.getArmedSamples)();
    if (armed.length === 0 || current !== "armed")
        return;
    const { cachedBidAsk, selectedMarket } = (0, state_1.getState)();
    if (!cachedBidAsk) {
        // No book on screen means there is nothing to record; stay armed.
        (0, notifications_1.showOrderError)("No orderbook prices to record yet");
        return;
    }
    const observed = {
        ...(armed[0].venue === "novig" && selectedMarket?.novigOutcomeIds
            ? { outcomeIds: selectedMarket.novigOutcomeIds } : {}),
        yesBid: cachedBidAsk.yesBid,
        yesAsk: cachedBidAsk.yesAsk,
        noBid: cachedBidAsk.noBid,
        noAsk: cachedBidAsk.noAsk,
        // Both timestamps are the client's own, so this age carries no clock
        // skew (see the server's ObservedQuote).
        bookAgeMs: Math.max(0, Date.now() - cachedBidAsk.timestamp),
    };
    render("pending");
    // One book, one instant: every click is marked with the same numbers.
    const outcomes = await Promise.all(armed.map((entry) => sendOne(entry, observed)));
    (0, price_sample_arm_1.disarmClicks)(outcomes.filter((o) => o.settled).map((o) => o.clickId));
    const legs = outcomes.reduce((sum, o) => sum + o.sampled, 0);
    const stillArmed = outcomes.filter((o) => !o.settled).length;
    if (stillArmed > 0) {
        (0, notifications_1.showOrderError)(legs > 0
            ? `Recorded ${legs} leg${legs === 1 ? "" : "s"}; ${stillArmed} order${stillArmed === 1 ? "" : "s"} failed — try again`
            : "Couldn't record this price — try again");
        render("armed");
        return;
    }
    if (legs > 0) {
        const orders = outcomes.length;
        (0, notifications_1.showOrderStatus)(`Price recorded for ${legs} leg${legs === 1 ? "" : "s"}` +
            (orders > 1 ? ` across ${orders} orders` : ""), "success");
        showResult("done");
        return;
    }
    // Settled, but nothing was eligible — no fills, or already marked.
    (0, notifications_1.showOrderError)("Nothing to record for those orders");
    showResult("error");
}
/** Wire the button up. Safe to call once at startup. */
function initPriceSampleButton() {
    const el = button();
    if (!el)
        return;
    el.addEventListener("click", () => {
        void handlePress();
    });
    (0, price_sample_arm_1.subscribeArmChange)((armed) => {
        // A new order arming must beat the lingering ✓ of the previous one —
        // otherwise the button reads "SAVED" while a fresh, unsampled order is
        // sitting there waiting to be marked.
        if (armed.length > 0) {
            clearLinger();
            render("armed");
            return;
        }
        syncFromArmState();
    });
    // Also re-check on app state changes: that is how a market switch (which
    // invalidates the arm) reaches the button.
    (0, state_1.subscribe)(() => syncFromArmState());
    syncFromArmState();
}
/** TEST-ONLY: drop the rendered state and any lingering result timer. */
function resetPriceSampleButtonForTest() {
    clearLinger();
    current = "hidden";
}
